import type { CSSProperties } from "react";
import {
  ArrowLeft,
  ArrowLeftRight,
  Baby,
  Bell,
  BedDouble,
  BookOpen,
  Car,
  Dumbbell,
  Gift,
  HeartPulse,
  House,
  Landmark,
  Laptop,
  PartyPopper,
  PawPrint,
  Plane,
  Repeat,
  ShoppingBag,
  ShoppingCart,
  Sofa,
  Sparkles,
  Utensils,
  Wrench,
  Zap,
  Calendar,
  ChartPie,
  Ellipsis,
  EllipsisVertical,
  Equal,
  Hash,
  MessageCircle,
  Pencil,
  Percent,
  Plus,
  Receipt,
  Scale,
  Settings,
  Trash2,
  UserPlus,
  Users,
  Wallet,
  ArrowRight,
  AtSign,
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Info,
  LoaderCircle,
  LogOut,
  Mail,
  Phone,
  QrCode,
  RefreshCw,
  Ticket,
  TriangleAlert,
  User,
  X,
  type LucideIcon,
} from "lucide-react";

/**
 * Qota usa il set Lucide (griglia 24px, tratto 2px, terminazioni tonde).
 *
 * SOSTITUZIONE RISPETTO AL DESIGN SYSTEM: l'originale legge `window.lucide`
 * caricato da CDN. In una PWA che deve funzionare offline non è accettabile —
 * al primo volo aereo le icone sparirebbero — e la CSP di una pagina pubblicata
 * bloccherebbe comunque lo script esterno.
 *
 * Qui le icone arrivano da `lucide-react`, quindi finiscono nel bundle, sono
 * tipizzate e restano tree-shakeable. Il registro esplicito serve proprio a
 * questo: un `import * as icons` includerebbe tutte e mille.
 *
 * Per aggiungerne una: importala qui sopra e mettila nel registro.
 */
const REGISTRY = {
  "arrow-left": ArrowLeft,
  // Un pagamento è un trasferimento, non un equilibrio: la bilancia
  // racconterebbe l'effetto invece del fatto.
  "arrow-left-right": ArrowLeftRight,
  "arrow-right": ArrowRight,
  "at-sign": AtSign,
  calendar: Calendar,
  "chart-pie": ChartPie,
  check: Check,
  "chevron-down": ChevronDown,
  "chevron-right": ChevronRight,
  "circle-alert": CircleAlert,
  "circle-check": CircleCheck,
  info: Info,
  "loader-circle": LoaderCircle,
  "log-out": LogOut,
  mail: Mail,
  "message-circle": MessageCircle,
  ellipsis: Ellipsis,
  "ellipsis-vertical": EllipsisVertical,
  equal: Equal,
  hash: Hash,
  pencil: Pencil,
  percent: Percent,
  plus: Plus,
  receipt: Receipt,
  scale: Scale,
  settings: Settings,
  "trash-2": Trash2,
  "user-plus": UserPlus,
  users: Users,
  wallet: Wallet,
  phone: Phone,
  "qr-code": QrCode,
  "refresh-cw": RefreshCw,
  ticket: Ticket,
  "triangle-alert": TriangleAlert,
  user: User,
  x: X,

  // Le icone delle categorie di spesa. Il registro di @qota/shared le nomina
  // per stringa, quindi un test verifica che ognuna esista davvero qui.
  baby: Baby,
  "bed-double": BedDouble,
  "book-open": BookOpen,
  car: Car,
  bell: Bell,
  dumbbell: Dumbbell,
  gift: Gift,
  "heart-pulse": HeartPulse,
  house: House,
  landmark: Landmark,
  laptop: Laptop,
  "party-popper": PartyPopper,
  "paw-print": PawPrint,
  plane: Plane,
  repeat: Repeat,
  "shopping-bag": ShoppingBag,
  "shopping-cart": ShoppingCart,
  sofa: Sofa,
  sparkles: Sparkles,
  utensils: Utensils,
  wrench: Wrench,
  zap: Zap,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof REGISTRY;

/** I nomi disponibili, per i test e per chi deve validare una stringa. */
export const ICON_NAMES = Object.keys(REGISTRY) as IconName[];

export function isIconName(value: string): value is IconName {
  return value in REGISTRY;
}

export interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  color?: string;
  style?: CSSProperties;
}

export function Icon({
  name,
  size = 20,
  strokeWidth = 2,
  color = "currentColor",
  style,
}: IconProps) {
  const Glyph = REGISTRY[name];

  return (
    <Glyph
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      color={color}
      style={{ display: "block", flexShrink: 0, ...style }}
      aria-hidden="true"
    />
  );
}
